import { useState } from "react";
import { Input } from "@/components/ui/input";
import { roleLabel } from "@/lib/roleLabels";
import { CheckCircleIcon, MagnifyingGlassIcon, PlusIcon } from "@phosphor-icons/react";

/*
  Who asked for a task: pick a colleague, or record a name from outside.

  REPLACES A NATIVE <datalist>, which did the job functionally — type anything,
  with the directory as suggestions — but rendered as the browser's own popup:
  a black box in Edge's dark styling, in a light-only app, with none of the
  grid's typography and no role hints. A native control was the wrong trade
  here; the whole point of the field is that somebody scans it for a name.

  Deliberately built to look like the Assignees checklist in the same dialogs
  (search box above a bordered, scrollable, divided list, selected row in
  #EFF6FF with a check) rather than as a Select. A Select cannot hold a value
  that is not one of its options, and the requester frequently is not — a plant
  manager, a vendor, somebody in another function with no account here. So the
  list is the fast path and typing is always available.

  Single-select: a task has one requester. `value` is TEXT, not an id, matching
  tasks.requested_by (see supabase/schema_task_requested_by.sql for why that
  column is text).
*/
export default function RequesterPicker({
  users = [],
  value = "",
  onChange,
  placeholder = "Search people, or type any name…",
  emptyMessage = "Nobody to suggest — type the requester's name above.",
  testId = "requester",
}) {
  const [query, setQuery] = useState("");

  const nameOf = (u) => u.name || u.email || "";
  const typed = query.trim();

  const matches = users.filter((u) => {
    if (!typed) return true;
    return `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(typed.toLowerCase());
  });

  // Is the current value a name from the directory, or one somebody typed? A
  // typed value has to be shown as a row of its own, or a requester from
  // outside the company would be selected with nothing on screen saying so.
  const inDirectory = (name) =>
    users.some((u) => nameOf(u).toLowerCase() === String(name || "").toLowerCase());
  const valueIsCustom = Boolean(value) && !inDirectory(value);

  // Offer the typed text only when it is not already a name in the list —
  // otherwise the same person appears twice, once as a colleague and once as
  // free text, which are different values as far as the column is concerned.
  const offerTyped = Boolean(typed) && !inDirectory(typed);

  const pick = (name) => {
    onChange(name);
    setQuery("");
  };

  return (
    <div data-testid={`${testId}-picker`}>
      <div className="relative mb-2">
        <MagnifyingGlassIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testId}-search`}
          className="rounded-sm h-9 pl-8 text-sm"
        />
      </div>

      <div
        className="border border-border rounded-sm max-h-32 overflow-y-auto divide-y divide-border"
        data-testid={`${testId}-list`}
      >
        {/* A typed name sits at the top once chosen, so the selection is always
            visible even when the search box has been cleared. */}
        {valueIsCustom && (
          <button
            type="button"
            onClick={() => pick("")}
            data-testid={`${testId}-selected-custom`}
            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm bg-[#EFF6FF]"
          >
            <span>
              {value}
              <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1">typed</span>
            </span>
            <CheckCircleIcon size={16} weight="fill" color="#0176D3" />
          </button>
        )}

        {offerTyped && (
          <button
            type="button"
            onClick={() => pick(typed)}
            data-testid={`${testId}-use-typed`}
            className="w-full flex items-center gap-1.5 px-3 py-2 text-left text-sm text-[#0176D3] hover:bg-zinc-50 transition-colors"
          >
            <PlusIcon size={12} weight="bold" />
            Use “{typed}”
          </button>
        )}

        {matches.map((u) => {
          const name = nameOf(u);
          const selected = String(value || "").toLowerCase() === name.toLowerCase();
          return (
            <button
              type="button"
              key={u._id || u.id || name}
              onClick={() => pick(selected ? "" : name)}
              data-testid={`${testId}-option-${u._id || u.id || name}`}
              className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${selected ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
            >
              <span>
                {name}
                {u.role && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1">{roleLabel(u.role)}</span>
                )}
              </span>
              {selected && <CheckCircleIcon size={16} weight="fill" color="#0176D3" />}
            </button>
          );
        })}

        {matches.length === 0 && !offerTyped && !valueIsCustom && (
          <div className="px-3 py-4 text-xs text-zinc-500" data-testid={`${testId}-empty`}>
            {emptyMessage}
          </div>
        )}
      </div>
    </div>
  );
}
